import { Box, Button, Card, Grid, Typography } from "@mui/material";
import { ListContextProvider, useDeleteWithConfirmController, useListContext, useListController } from "ra-core";
import { FC, useCallback, useState } from "react";
import InfoIcon from "@mui/icons-material/Info";
import AddIcon from "@mui/icons-material/Add";
import { Content } from "~/components/content/Content";
import { FunctionField, TextField } from "~/components/field";
import { Datagrid } from "~/components/list/datagrid";
import Scrollbar from "~/components/Scrollbar";
import RowExpandClassificationRuleEdit from "~/components/securefiles/RowExpandClassificationRuleEdit";
import { OdResourcePageProps } from "~/types/resource";
import { Pagination as DefaultPagination } from '~/components/list/pagination'
import { ClassificationRuleAmbinetDataCotnextProvider, useClassificationRuleAmbinetDataCotnext } from "~/components/securefiles/ClassificationRuleAmbientDataProvider";
import ClassificationLabel from "~/components/governance/labels/ClassificationLabel";
import { RecPatch } from "~/dataProvider/tools";
import { ClassificationRuleDto } from "~/gen/api/securefile";
import Confirm from "~/components/widgets/Confirm";
import { useDefaultSavedHandlers } from "~/controller/defaultSavedHandlers";
import { push } from "connected-react-router";
import { useDispatch } from "react-redux";


const ClassificationRules: FC<OdResourcePageProps> = (props) => {
  const listCtrl = useListController({ basePath: '', filter: {}, })
  
  return (
    <ClassificationRuleAmbinetDataCotnextProvider>
      <ListContextProvider value={listCtrl}>
        <ClassificationRulesBody {...props} />
      </ListContextProvider>
    </ClassificationRuleAmbinetDataCotnextProvider>
  )
}


const ClassificationRulesBody: FC<OdResourcePageProps> = ({
  options, resource
}) => {
  const listCtx = useListContext()
  const { refetch } = listCtx
  const dispatch = useDispatch()
  const { classifications } = useClassificationRuleAmbinetDataCotnext()

  const [states, setStates] = useState({
    recordToDel: null as ClassificationRuleDto|null,
  })

  const delHandlers = useDefaultSavedHandlers('Delete Classification Rule')
  delHandlers.onSuccess.merge(useCallback(() => {
    refetch && refetch()
  }, [refetch]))

  const {
    open: delConfirmOpen,
    handleDialogOpen,
    handleDialogClose,
    handleDelete,
    loading: delSaving,
  } = useDeleteWithConfirmController({
    resource,
    record: states.recordToDel as any,
    mutationMode: 'pessimistic',
    ...delHandlers,
  });

  const setRuleToBeDeleted = useCallback((r:ClassificationRuleDto) => {
    setStates(v => ({...v, recordToDel: r}))
    handleDialogOpen({stopPropagation:()=>{}} as any)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <Content pageName="Classification Rules" resourceName="Classification Rules" breadcrumbs={options?.breadcrumbs}
      mainActions={
        <Button
          color="primary"
          startIcon={<AddIcon fontSize="small" />}
          variant="contained"
          onClick={() => dispatch(push(`/${resource}/create`))}
        >
          Create Rule
        </Button>
      }
    >
      <Grid container spacing={4} sx={{ mt: 2 }} >
        <Grid item xs={12}>
          <Card>
            <Scrollbar>
              <Box sx={{ minWidth: 1150 }}>
                <Datagrid
                  hasBulkActions={false} 
                  expand={<RowExpandClassificationRuleEdit setRuleToBeDeleted={setRuleToBeDeleted} />}
                  sx={{
                    '& th' : { py: 1, textTransform: 'uppercase' },
                    '& td' : { py: 2 },
                    '& span.MuiTypography-root.filename' : { maxWidth:400, wordBreak:'break-word' },
                  }}
                  {...listCtx}
                >
                  <TextField label="Name" source="displayName" sortable={false} />
                  <TextField label="Comment" source="comment" sortable={false} />
                  <FunctionField source="targetClassification" label="Classification" sortable={false} 
                    render={(d?:ClassificationRuleDto&RecPatch) => {
                      const clss = classifications.find(c => c.secondaryClassificationId === d?.targetClassification)
                      if (!clss) { return null }
                      return <ClassificationLabel variant={clss.primaryClassification!} text={clss.name!} />;
                    }}
                  />
                  <FunctionField label="Priority" source="priority" sortable={false} 
                    render={(d?:ClassificationRuleDto&RecPatch) => {
                      return (
                        <Typography variant="body2">
                          {d?.priority}
                          {!(d?.activeRule) &&
                            <Typography color="error"> / Inactive</Typography>}
                        </Typography>)
                    }}
                  />
                </Datagrid>
              </Box>
            </Scrollbar>
            {/* {//@ts-ignore
              <DefaultPagination {...listCtx} />} */}
            {/* do we really want a pager ?? */}
          </Card>
          <Confirm
            isOpen={delConfirmOpen}
            loading={delSaving}
            title={"Delete Classification Rule"}
            onConfirm={handleDelete}
            onClose={(e:any) => {
              handleDialogClose(e)
              setStates(v => ({...v, recordToDel:null}))
            }}
            content={
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <InfoIcon fontSize="large" sx={{ color: 'info.main' }}/>
                <Typography
                  component="span"
                  variant="body2"
                  sx={{ ml: 1 }}
                >
                  Please confirm whether to proceed with the deletion of this
                  {` "${states.recordToDel?.displayName}" `}
                  Classification Rule.
                </Typography>
              </Box>
            }
          />
        </Grid>
      </Grid>
    </Content>
  )
}

export default ClassificationRules;
